"use client";

import { mergeProps } from "@base-ui/react/merge-props";
import { useRender } from "@base-ui/react/use-render";
import { ChevronsLeftRightIcon } from "lucide-react";
import * as React from "react";

import { cn } from "../cn";
import { Badge } from "./badge";
import { CountPill } from "./count-pill";
import { FOCUS_RING } from "./focus-ring";
import { CARD_HOVER } from "./states";

// Design spec §6.5: board columns 300 wide with a 16 px gutter, hairline cards with no shadow.

/** The horizontal scroller that holds the columns. */
export function Board({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="board"
      className={cn("flex min-h-0 items-start gap-4 overflow-x-auto pb-2", className)}
      {...props}
    />
  );
}

export type BoardColumnProps = Omit<React.ComponentProps<"section">, "title"> & {
  title: string;
  count?: number;
  /** Header buttons ("+", "…"), revealed on hover or focus (always on coarse pointers). */
  actions?: React.ReactNode;
  /** A 40 px rail with the title written vertically; `onCollapsedChange` adds the toggle. */
  collapsed?: boolean;
  onCollapsedChange?: (collapsed: boolean) => void;
};

export function BoardColumn({
  title,
  count,
  actions,
  collapsed = false,
  onCollapsedChange,
  className,
  children,
  ...props
}: BoardColumnProps) {
  const headingId = React.useId();
  if (collapsed) {
    return (
      <section
        data-slot="board-column"
        data-collapsed=""
        aria-labelledby={headingId}
        className={cn(
          "flex w-10 shrink-0 flex-col items-center gap-2 rounded-lg bg-sunken py-2",
          className,
        )}
        {...props}
      >
        {onCollapsedChange ? (
          <button
            type="button"
            aria-label={`Expand ${title}`}
            onClick={() => onCollapsedChange(false)}
            className={cn(
              "inline-flex size-7 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground",
              FOCUS_RING,
            )}
          >
            <ChevronsLeftRightIcon className="size-4" aria-hidden={true} />
          </button>
        ) : null}
        <h3
          id={headingId}
          className="text-sm font-medium text-secondary [writing-mode:vertical-rl]"
        >
          {title}
        </h3>
        {count !== undefined ? <CountPill count={count} /> : null}
      </section>
    );
  }
  return (
    <section
      data-slot="board-column"
      aria-labelledby={headingId}
      className={cn("group/column flex w-(--board-col-w) shrink-0 flex-col", className)}
      {...props}
    >
      <header className="mb-1 flex h-9 items-center gap-1.5 px-1">
        <h3
          id={headingId}
          className="inline-flex h-6 items-center rounded-sm bg-secondary px-2 text-sm font-medium text-secondary"
        >
          {title}
        </h3>
        {count !== undefined ? <CountPill count={count} /> : null}
        <span className="ml-auto flex items-center gap-0.5 opacity-0 transition-opacity duration-(--dur-fast) group-hover/column:opacity-100 group-focus-within/column:opacity-100 pointer-coarse:opacity-100">
          {actions}
          {onCollapsedChange ? (
            <button
              type="button"
              aria-label={`Collapse ${title}`}
              onClick={() => onCollapsedChange(true)}
              className={cn(
                "inline-flex size-7 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground",
                FOCUS_RING,
              )}
            >
              <ChevronsLeftRightIcon className="size-4" aria-hidden={true} />
            </button>
          ) : null}
        </span>
      </header>
      <div className="flex flex-col">{children}</div>
    </section>
  );
}

const CARD_FOCUS =
  "has-[[data-slot=board-card-title]:focus-visible]:outline-2 has-[[data-slot=board-card-title]:focus-visible]:outline-solid has-[[data-slot=board-card-title]:focus-visible]:outline-offset-2 has-[[data-slot=board-card-title]:focus-visible]:outline-ring";

export type BoardCardProps = Omit<React.ComponentProps<"article">, "title"> & {
  title: React.ReactNode;
  href?: string;
  onSelect?: () => void;
  /** Router link element for the title; takes precedence over `href`. */
  render?: React.ReactElement;
  /** Up to three are shown, then "+n". */
  chips?: React.ReactNode[];
  /** Footer, left: `AvatarStack xs`. */
  avatars?: React.ReactNode;
  due?: React.ReactNode;
  priority?: React.ReactNode;
  /** Footer, right: `MetaCounts`. */
  meta?: React.ReactNode;
  selected?: boolean;
  dragging?: boolean;
  /** The drag ghost: rotated half a degree with --shadow-md. */
  ghost?: boolean;
  /** TASK-FILES: cover image URLs (the first four), a grid above the title. */
  covers?: string[];
};

export function BoardCard({
  title,
  href,
  onSelect,
  render,
  chips,
  avatars,
  due,
  priority,
  meta,
  selected = false,
  dragging = false,
  ghost = false,
  covers,
  className,
  ...props
}: BoardCardProps) {
  const interactive = Boolean(render || href || onSelect);
  const titleEl = useRender({
    defaultTagName: href || render ? "a" : onSelect ? "button" : "span",
    render,
    props: mergeProps<"a">(
      {
        className: cn(
          "line-clamp-3 text-left text-base text-foreground outline-none",
          interactive && "cursor-pointer after:absolute after:inset-0 after:content-['']",
        ),
        ...(href ? { href } : {}),
        ...(onSelect ? { onClick: onSelect } : {}),
        ...(!href && !render && onSelect ? ({ type: "button" } as object) : {}),
        ...(selected && interactive ? { "aria-current": "true" as const } : {}),
        // `useListKeys` (j / k / Enter) lands on the title, the card's one focus stop.
        ...({ "data-row-focus": "" } as object),
      },
      { children: title },
    ),
    state: { slot: "board-card-title" },
  });
  const shownChips = chips?.slice(0, 3) ?? [];
  const moreChips = (chips?.length ?? 0) - shownChips.length;
  const hasFooter = Boolean(avatars || due || priority || meta);
  return (
    <article
      data-slot="board-card"
      data-selected={selected || undefined}
      data-dragging={dragging || undefined}
      data-ghost={ghost || undefined}
      className={cn(
        "relative mb-2 rounded-lg border border-border bg-card p-3 text-card-foreground data-dragging:opacity-40 data-ghost:rotate-[0.5deg] data-ghost:shadow-md data-selected:border-primary data-selected:bg-primary-soft",
        CARD_HOVER,
        "data-selected:hover:border-primary",
        CARD_FOCUS,
        className,
      )}
      {...props}
    >
      {covers?.length ? (
        <div
          data-slot="board-card-covers"
          className={cn(
            "-mx-3 -mt-3 mb-2 grid h-28 gap-px overflow-hidden rounded-t-lg bg-border",
            covers.length > 1 ? "grid-cols-2" : "grid-cols-1",
            covers.length > 2 ? "grid-rows-2" : "grid-rows-1",
          )}
        >
          {covers.slice(0, 4).map((src, i) => (
            <img
              key={src}
              src={src}
              alt=""
              className={cn(
                "size-full bg-sunken object-cover",
                covers.length === 3 && i === 0 && "row-span-2",
              )}
            />
          ))}
        </div>
      ) : null}
      {titleEl}
      {shownChips.length ? (
        <div className="relative z-10 mt-2 flex flex-wrap items-center gap-1">
          {shownChips.map((chip, i) => (
            // Chips are positional; the list never reorders.
            // biome-ignore lint/suspicious/noArrayIndexKey: positional, static per render
            <React.Fragment key={i}>{chip}</React.Fragment>
          ))}
          {moreChips > 0 ? <Badge variant="neutral">+{moreChips}</Badge> : null}
        </div>
      ) : null}
      {hasFooter ? (
        <div
          className={cn(
            "mt-2 flex h-6 items-center gap-2 text-xs tabular-nums",
            // Muted on --primary-soft is under 4.5 in dark; selected cards lift to secondary.
            selected ? "text-secondary" : "text-muted-foreground",
          )}
        >
          {avatars ? <span className="relative z-10 flex">{avatars}</span> : null}
          {due ? <span>{due}</span> : null}
          {priority ? <span className="flex items-center">{priority}</span> : null}
          {meta ? <span className="ml-auto flex items-center">{meta}</span> : null}
        </div>
      ) : null}
    </article>
  );
}
